import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EyeOff, Eye, FileJson } from "lucide-react";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { CardHeader } from "../../../components/cards";
import { SendIcon } from "../../../components/icons";
import { useCustomer, useToggleCustomerHidden } from "../hooks";
import { SitePreviewCard, SiteGenerationForm, CustomerJsonExportModal } from "../components";
import { CreatedSitesHistory, ManualCreatedSiteForm } from "../../created-sites/components";
import { ContactsList } from "../../contacts/components";
import { ReportForm, ReportsList } from "../../reports/components";
import { ArticleForm, ArticlesList } from "../../articles/components";
import { ProspectionForm, ProspectionsList } from "../../prospections/components";
import { MailMessagesList, SendMailMessageForm } from "../../mail-messages/components";
import { useMe } from "../../users/hooks/useMe";
import { CustomerStatus, CreatedSiteStatus, UserRole } from "../../../__generated__/globalTypes";
import { customerStatusLabel, findBySourceLabel } from "../../../helpers/enum-labels";

type ICustomerParams = {
  id: string;
};

export const Customer: React.FC = () => {
  const { id } = useParams<ICustomerParams>();
  const navigate = useNavigate();
  const [reportsKey, setReportsKey] = React.useState(0);
  const [articlesKey, setArticlesKey] = React.useState(0);
  const [prospectionsKey, setProspectionsKey] = React.useState(0);
  const [mailMessagesKey, setMailMessagesKey] = React.useState(0);

  useEffect(() => {
    if (!id) {
      navigate("/");
    }
  }, []);

  const { data, loading, refetch } = useCustomer(+id!);
  const { data: meData } = useMe();
  const { toggleHidden, loading: isTogglingHidden } = useToggleCustomerHidden();
  const [showJsonExport, setShowJsonExport] = React.useState(false);
  const customer = data?.customer?.result;
  const isEmployee = meData?.me?.role === UserRole.Employee;
  const isAdmin = meData?.me?.role === UserRole.Admin;

  if (loading) return <Loading />;

  const handleToggleHidden = () => {
    if (!customer) return;
    const nextHidden = !customer.hidden;
    if (
      !window.confirm(
        nextHidden
          ? "Masquer ce client ? Il n'apparaîtra plus dans les listes par défaut."
          : "Réafficher ce client ?"
      )
    ) {
      return;
    }
    toggleHidden(customer.id, nextHidden);
  };

  const createdSites = customer?.createdSites ?? [];
  const lastDoneSite = [...createdSites]
    .filter((s) => s.status === CreatedSiteStatus.Done)
    .sort((a, b) => b.id - a.id)[0];

  const siteUrl = lastDoneSite?.url
    ? lastDoneSite.url.startsWith("http")
      ? lastDoneSite.url
      : `https://${lastDoneSite.url}`
    : null;

  const currentSiteUrl = customer?.site
    ? customer.site.startsWith("http")
      ? customer.site
      : `https://${customer.site}`
    : null;

  return (
    <>
      <Header
        title="Client"
        subtitle={
          <span className="flex items-center gap-2">
            {customer?.name}
            {customer && (
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                  customer.status === CustomerStatus.Converti
                    ? "bg-green-100 text-green-700"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {customerStatusLabel(customer.status)}
              </span>
            )}
            {customer?.analyseDone && (
              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                Analyse effectuée
              </span>
            )}
            {customer?.problemsDetected && (
              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                Problèmes détectés
              </span>
            )}
          </span>
        }
        buttons={[
          {
            actionText: "Modifier",
            bgColor: "indigo",
            textColor: "white",
            link: `/customer/${id}/update`,
            icon: <SendIcon />,
          },
        ]}
      />
      <div className="main-container">
        <div className="flex justify-end gap-2 mb-4">
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowJsonExport(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
              <FileJson size={16} />
              Exporter en JSON
            </button>
          )}
          <button
            type="button"
            onClick={handleToggleHidden}
            disabled={isTogglingHidden}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            {customer?.hidden ? <Eye size={16} /> : <EyeOff size={16} />}
            {customer?.hidden ? "Réafficher le client" : "Cacher le client"}
          </button>
        </div>
        {showJsonExport && customer && (
          <CustomerJsonExportModal customer={customer} onClose={() => setShowJsonExport(false)} />
        )}
        <div className="section">
          <div className="element">
            <div className="card">
              <CardHeader title="Informations générales" />
              <div className="w-full mt-3">
                <p className="label">Nom</p>
                <input className="input w-full" value={customer?.name} disabled />
              </div>
              <div className="flex">
                <div className="w-1/2 mr-1">
                  <div className="w-full mt-3">
                    <p className="label">Email</p>
                    <input className="input w-full" value={customer?.email ?? ""} disabled />
                  </div>
                </div>
                <div className="w-1/2 ml-1">
                  <div className="w-full mt-3">
                    <p className="label">Téléphone</p>
                    <input className="input w-full" value={customer?.phone ?? ""} disabled />
                  </div>
                </div>
              </div>
              <div className="flex">
                <div className="w-1/2 mr-1">
                  <div className="w-full mt-3">
                    <p className="label">Site</p>
                    <input className="input w-full" value={customer?.site ?? ""} disabled />
                  </div>
                </div>
                <div className="w-1/2 ml-1">
                  <div className="w-full mt-3">
                    <p className="label">Trouvé via</p>
                    <input
                      className="input w-full"
                      value={findBySourceLabel(customer?.findBy)}
                      disabled
                    />
                  </div>
                </div>
              </div>
              <div className="w-full mt-3">
                <p className="label">Adresse</p>
                <input className="input w-full" value={customer?.address ?? ""} disabled />
              </div>
              <div className="flex">
                <div className="w-1/2 mr-1">
                  <div className="w-full mt-3">
                    <p className="label">Code postal</p>
                    <input className="input w-full" value={customer?.postalCode ?? ""} disabled />
                  </div>
                </div>
                <div className="w-1/2 ml-1">
                  <div className="w-full mt-3">
                    <p className="label">Ville</p>
                    <input className="input w-full" value={customer?.city ?? ""} disabled />
                  </div>
                </div>
              </div>
              <div className="flex">
                <div className="w-1/2 mr-1">
                  <div className="w-full mt-3">
                    <p className="label">Catégorie</p>
                    <input
                      className="input w-full"
                      value={customer?.category?.name ?? ""}
                      disabled
                    />
                  </div>
                </div>
                <div className="w-1/2 ml-1">
                  <div className="w-full mt-3">
                    <p className="label">CMS</p>
                    <input className="input w-full" value={customer?.cms?.name ?? ""} disabled />
                  </div>
                </div>
              </div>
              <div className="w-full mt-3">
                <p className="label">Notes / À faire</p>
                <textarea className="input w-full" value={customer?.todo ?? ""} disabled />
              </div>
              <div className="flex gap-2 mt-3">
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    customer?.analyseDone
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-400"
                  }`}
                >
                  {customer?.analyseDone ? "Analyse effectuée" : "Analyse non faite"}
                </span>
                {customer?.problemsDetected && (
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    Problèmes détectés
                  </span>
                )}
              </div>
            </div>

            <div className="card mt-4">
              <CardHeader
                title="Contacts"
                button={{
                  title: "Nouveau contact",
                  url: `/contact/create?customerId=${id}`,
                }}
              />
              <ContactsList customerId={+id!} />
            </div>
          </div>

          <div className="element">
            <SitePreviewCard
              title="Site actuel"
              url={currentSiteUrl}
              emptyText="Aucun site"
            />
          </div>
        </div>

        {!isEmployee && (
          <div className="full card mt-4">
            <CardHeader title="Générer le site" />
            <SiteGenerationForm customerId={+id!} onGenerated={() => refetch()} />
          </div>
        )}

        <div className="section mt-4">
          <div className="element">
            <div className="card">
              <CardHeader title="Historique des générations" />
              <div className="mt-3">
                <CreatedSitesHistory sites={createdSites} isAdmin={isAdmin} />
              </div>
            </div>

            {isAdmin && (
              <div className="card mt-4">
                <CardHeader
                  title="Ajouter un site existant"
                  subtitle="Rattacher manuellement un site créé hors du flux Hermes"
                />
                <ManualCreatedSiteForm customerId={+id!} onCreated={() => refetch()} />
              </div>
            )}
          </div>

          <div className="element">
            <SitePreviewCard
              title="Aperçu du dernier site généré"
              url={siteUrl}
              emptyText="Aucun site généré pour l'instant"
            />
          </div>
        </div>

        <div className="full card mt-4">
          <CardHeader title="Prospections" />
          <ProspectionForm
            customerId={+id!}
            onCompleted={() => setProspectionsKey((k) => k + 1)}
          />
          <div className="mt-4">
            <ProspectionsList key={prospectionsKey} customerId={+id!} />
          </div>
        </div>

        <div className="section mt-4">
          <div className="element">
            <div className="card">
              <CardHeader
                title="Emails"
                subtitle={
                  customer?.automaticEmailsSentCount
                    ? `${customer.automaticEmailsSentCount} email${
                        customer.automaticEmailsSentCount > 1 ? "s" : ""
                      } automatique${customer.automaticEmailsSentCount > 1 ? "s" : ""} envoyé${
                        customer.automaticEmailsSentCount > 1 ? "s" : ""
                      }`
                    : undefined
                }
              />
              <SendMailMessageForm
                customerId={+id!}
                defaultTo={customer?.email ?? ""}
                onCompleted={() => setMailMessagesKey((k) => k + 1)}
              />
              <div className="mt-4">
                <MailMessagesList key={mailMessagesKey} customerId={+id!} />
              </div>
            </div>
          </div>

          <div className="element">
            {!isEmployee && (
              <div className="card">
                <CardHeader title="Rapports SEO" />
                <ReportForm
                  customerId={+id!}
                  onCompleted={() => setReportsKey((k) => k + 1)}
                />
                <div className="mt-4">
                  <ReportsList key={reportsKey} customerId={+id!} />
                </div>
              </div>
            )}

            {customer?.status === CustomerStatus.Converti && (
              <div className={isEmployee ? "card" : "card mt-4"}>
                <CardHeader title="Articles" />
                <ArticleForm
                  customerId={+id!}
                  onCompleted={() => setArticlesKey((k) => k + 1)}
                />
                <div className="mt-4">
                  <ArticlesList key={articlesKey} customerId={+id!} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
